import { useState, useEffect, useRef } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { X } from 'lucide-react'
import SideNav from './SideNav'
import TopNav from './TopNav'
import HelpPanel from '../HelpPanel'
import CommandPalette from '../CommandPalette'
import KeyboardHints from '../KeyboardHints'
import { api } from '../../lib/api'
import { useKeyboardShortcuts } from '../../lib/useKeyboardShortcuts'

function useTheme() {
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    const stored = localStorage.getItem('cto-theme')
    return (stored === 'light' ? 'light' : 'dark') as 'dark' | 'light'
  })

  useEffect(() => {
    if (theme === 'light') {
      document.documentElement.classList.add('light')
    } else {
      document.documentElement.classList.remove('light')
    }
    localStorage.setItem('cto-theme', theme)
  }, [theme])

  const toggle = () => setTheme(t => (t === 'dark' ? 'light' : 'dark'))
  return { theme, toggle }
}

// ── Navigation commands ──────────────────────────────────────────────────────

const NAV_COMMANDS: Record<string, string> = {
  '/home': '/app', '/dashboard': '/app', '/briefing': '/app/briefing',
  '/signals': '/app/signals', '/incidents': '/app/incidents',
  '/terminal': '/app/terminal', '/workspace': '/app/workspace',
  '/trends': '/app/trends', '/charts': '/app/trend-charts',
  '/hype': '/app/hype-cycle', '/watchlist': '/app/watchlist',
  '/watch': '/app/watchlist', '/competitors': '/app/competitors',
  '/sentiment': '/app/sentiment', '/news': '/app/tech-news',
  '/cyber': '/app/cyber', '/warroom': '/app/cyber-warroom',
  '/threats': '/app/cyber-warroom', '/world': '/app/world',
  '/ai': '/app/ai-adoption', '/regulatory': '/app/regulatory',
  '/cloud': '/app/cloud', '/internet': '/app/internet-health',
  '/supply': '/app/supply-chain', '/energy': '/app/energy',
  '/innovation': '/app/innovation', '/startups': '/app/startups',
  '/manda': '/app/manda', '/opensource': '/app/opensource',
  '/talent': '/app/talent', '/integrations': '/app/integrations',
  '/admin': '/app/admin',
  '/pricing': '/app/pricing',
  '/feeds': '/app/feed-manager',
  '/wiki': '/app/wiki',
  '/docs': '/app/api-docs',
  '/api': '/app/api-docs',
  '/price-trends': '/app/pricing-trends',
  '/foryou': '/app/for-you',
  '/radar': '/app/tech-radar',
  '/activity': '/app/activity',
  '/settings': '/app/settings',
  '/bookmarks': '/app/bookmarks',
  '/notes': '/app/notes',
  '/changed': '/app/what-changed',
  '/compare': '/app/vendor-comparison',
  '/vendors': '/app/vendor-comparison',
  '/outages': '/app/outage-history',
  '/compliance': '/app/compliance',
  '/earnings': '/app/earnings',
  '/stories': '/app/top-stories',
}


// ── Global Command Bar ───────────────────────────────────────────────────────

function CommandBar() {
  const navigate = useNavigate()
  const [input, setInput] = useState('')
  const [result, setResult] = useState('')
  const [processing, setProcessing] = useState(false)
  const [history, setHistory] = useState<string[]>([])
  const [histIdx, setHistIdx] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === '/' && document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
        e.preventDefault()
        inputRef.current?.focus()
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        inputRef.current?.focus()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  const execute = async (cmd: string) => {
    const trimmed = cmd.trim()
    if (!trimmed) return
    setHistory(prev => [trimmed, ...prev.slice(0, 30)])
    setHistIdx(-1)
    setProcessing(true)
    setResult('')

    try {
      if (trimmed.startsWith('/')) {
        const navCmd = trimmed.split(' ')[0].toLowerCase()
        if (NAV_COMMANDS[navCmd]) {
          navigate(NAV_COMMANDS[navCmd])
          setResult(`→ ${navCmd}`)
          setProcessing(false)
          return
        }
      }

      const parts = trimmed.split(/\s+/)
      const command = parts[0].toLowerCase()
      const args = parts.slice(1).join(' ')

      switch (command) {
        case 'search': {
          if (!args) { setResult('Usage: search <query>'); break }
          const { data } = await api.get('/api/search', { params: { q: args, limit: 5 } })
          setResult(`${data.total} results for "${args}"`)
          break
        }
        case 'entity': {
          if (!args) { setResult('Usage: entity <name>'); break }
          const { data } = await api.get(`/api/trends/entity/${encodeURIComponent(args)}`)
          setResult(`${data.entity}: ${data.total_mentions} mentions`)
          break
        }
        case 'threat': {
          const { data } = await api.get('/api/cyber/threat-level')
          setResult(`Threat: ${data.level?.toUpperCase()} | CVEs: ${data.critical_cves_7d} | Exploited: ${data.kev_additions_7d}`)
          break
        }
        case 'cloud': {
          const { data } = await api.get('/api/cloud/status')
          const issues = (data.providers || []).filter((p: any) => p.overall_status !== 'operational')
          setResult(issues.length === 0 ? `All ${data.providers?.length} providers OK` : `${issues.length} issues: ${issues.map((p: any) => p.name).join(', ')}`)
          break
        }
        case 'help':
          setResult('/cloud /cyber /news /trends /world /workspace /warroom | search, entity, threat, cloud, cves, trends')
          break
        default:
          if (NAV_COMMANDS[`/${command}`]) {
            navigate(NAV_COMMANDS[`/${command}`])
            setResult(`→ /${command}`)
          } else {
            setResult(`Unknown: ${command}`)
          }
      }
    } catch (err: any) {
      setResult(`Error: ${err.message}`)
    } finally {
      setProcessing(false)
    }
  }

  return (
    <div className="h-8 border-t border-amber/20 bg-[#0d0d0d] flex items-center px-3 gap-2 shrink-0">
      <span className="text-amber text-[10px] font-mono font-bold">{'>'}</span>
      <input
        ref={inputRef} type="text" value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' && !processing) { execute(input); setInput('') }
          else if (e.key === 'ArrowUp') { e.preventDefault(); if (history.length > 0) { const idx = Math.min(histIdx + 1, history.length - 1); setHistIdx(idx); setInput(history[idx]) } }
          else if (e.key === 'ArrowDown') { e.preventDefault(); if (histIdx > 0) { setHistIdx(histIdx - 1); setInput(history[histIdx - 1]) } else { setHistIdx(-1); setInput('') } }
          else if (e.key === 'Escape') { inputRef.current?.blur() }
        }}
        disabled={processing}
        className="flex-1 bg-transparent text-amber border-none outline-none font-mono text-[11px] font-medium"
        placeholder="/ to navigate, or: search, entity, threat, cloud..."
        title="Command bar" autoComplete="off" spellCheck={false}
      />
      {result && <span className="text-emerald/70 text-[10px] font-mono truncate max-w-[300px] hidden sm:block">{result}</span>}
      {processing && <span className="text-amber/50 text-[9px] animate-pulse">...</span>}
    </div>
  )
}

// ── Shell ─────────────────────────────────────────────────────────────────────

export default function Shell() {
  const { theme, toggle: toggleTheme } = useTheme()
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const location = useLocation()
  useKeyboardShortcuts()

  // Close mobile menu on navigation
  useEffect(() => { setMobileMenuOpen(false) }, [location.pathname])

  // Defensive: exit browser fullscreen whenever the route changes — a sticky
  // fullscreen left over from /app/war-room-globe was visually hiding the
  // sidebar on subsequent pages (Shell renders normally, but the fullscreen
  // viewport made the page chrome appear cropped).
  useEffect(() => {
    if (typeof document !== 'undefined' && document.fullscreenElement) {
      document.exitFullscreen().catch(() => { /* no-op */ })
    }
  }, [location.pathname])

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <TopNav
        theme={theme}
        onToggleTheme={toggleTheme}
        onToggleSidebar={() => {
          // On mobile, toggle the overlay menu
          if (window.innerWidth < 768) {
            setMobileMenuOpen(m => !m)
          } else {
            setSidebarCollapsed(c => !c)
          }
        }}
      />
      <div className="flex flex-1 overflow-hidden relative">
        {/* Desktop sidebar */}
        <aside
          className={`${
            sidebarCollapsed ? 'w-16' : 'w-48'
          } border-r border-border bg-surface overflow-y-auto transition-all duration-200 hidden md:block shrink-0`}
        >
          <SideNav collapsed={sidebarCollapsed} />
        </aside>

        {/* Mobile slide-out menu */}
        {mobileMenuOpen && (
          <>
            <div className="fixed inset-0 bg-black/60 z-40 md:hidden" onClick={() => setMobileMenuOpen(false)} />
            <aside className="fixed inset-y-0 left-0 w-64 bg-surface border-r border-border z-50 md:hidden overflow-y-auto animate-slide-in">
              <div className="flex items-center justify-between p-3 border-b border-border">
                <span className="text-xs font-semibold text-text-primary">TechDashboard</span>
                <button type="button" onClick={() => setMobileMenuOpen(false)} className="btn-ghost p-1 rounded" title="Close menu">
                  <X size={16} />
                </button>
              </div>
              <SideNav collapsed={false} />
            </aside>
          </>
        )}

        <main className="flex-1 overflow-y-auto p-3 sm:p-4 bg-base">
          <Outlet />
        </main>
      </div>
      <CommandBar />
      <CommandPalette />
      <KeyboardHints />
      <HelpPanel />
    </div>
  )
}
